import Link from "next/link"
import { ProductPickCard } from "@/components/tools/shared/product-pick-card"
import type { ProductRecommendationLink } from "@/lib/product-picks/types"
import type { PickBadge } from "@/data/article-product-picks"

export interface CompareBuyPanelItem {
  product: ProductRecommendationLink
  badge?: PickBadge
}

interface CompareBuyPanelProps {
  heading: string
  items: CompareBuyPanelItem[]
  /** Compare slug, used as the analytics article_slug (e.g. "compare:bermuda-vs-zoysia"). */
  slug: string
}

/**
 * Dual (or up-to-4) affiliate product panel for /compare pages. Reuses ProductPickCard
 * (image + name + reason + price + Buy) and fires affiliate_link_clicked with the
 * dedicated `compare-picks` placement. Design refs: IKEA/Walmart compare cards,
 * Relevance AI "Alternatives Compared". Renders nothing when no products resolve.
 */
export function CompareBuyPanel({ heading, items, slug }: CompareBuyPanelProps) {
  const resolved = items.filter((item) => item.product.affiliateUrl)
  if (resolved.length === 0) return null

  return (
    <div>
      <h2 className="cmp-block__h">{heading}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {resolved.map((item, i) => (
          <ProductPickCard
            key={item.product.asin ?? item.product.name}
            product={item.product}
            pickIndex={i}
            variant={i === 0 ? "primary" : "alternative"}
            trackingSlug={`compare:${slug}`}
            sourceContext="compare-buy-panel"
            placement="compare-picks"
            badge={item.badge}
            showPrice
          />
        ))}
      </div>
      <p className="mt-4 text-[11px] text-slate-500">
        As an Amazon Associate we earn from qualifying purchases.{" "}
        <Link
          href="/legal#affiliate-disclaimer"
          className="text-inherit no-underline hover:underline"
        >
          Disclosure
        </Link>
        .
      </p>
    </div>
  )
}
